/**
 * Shared low-cardinality bucketing for telemetry counts.
 *
 * Used by both the renderer (`app:startup_ready` tab/connection buckets) and
 * the electron main process (`app:resource_sample` tabCountBucket) — keep the
 * boundaries FROZEN across releases: the 1.4.17 distributions are the baseline
 * the 1.4.18 rollout watch compares against, and a boundary change re-buckets
 * the population invisibly.
 */

export type CountBucket = '0' | '1-3' | '4-10' | '11+';

export function bucketCount(n: number): CountBucket {
  if (n <= 0) return '0';
  if (n <= 3) return '1-3';
  if (n <= 10) return '4-10';
  return '11+';
}

/**
 * Bucketed wall-clock durations. Extracted from `feature:ssts_ready`'s private
 * `bucket()` (`electron/mssql/ensureSqlToolsReady.ts`) so main and renderer
 * share ONE set of edges rather than each connect/readiness event inventing
 * its own — the edges are FROZEN for the same reason `bucketCount`'s are:
 * moving one re-buckets the whole population invisibly and breaks comparison
 * against every release already measured.
 */
export type DurationBucket = 'lt5s' | '5to15s' | '15to60s' | 'gt60s';

/**
 * Bucket a measured duration in milliseconds.
 *
 * A NaN or negative input clamps to the lowest bucket rather than falling
 * through to `gt60s`: broken clock arithmetic must not be reported as the
 * worst-case latency.
 */
export function durationBucket(durationMs: number): DurationBucket {
  // Written as a negated `>=` so NaN takes this branch.
  if (!(durationMs >= 5_000)) return 'lt5s';
  if (durationMs < 15_000) return '5to15s';
  if (durationMs < 60_000) return '15to60s';
  return 'gt60s';
}

/**
 * Sub-second-resolution duration buckets shared by every connect-latency
 * measurement — `feature:connect` (`connectionIpcPolicy.ts`), `readyBucket` on
 * `connection:connect` (`connectionStore.ts`), and `feature:explorer_databases_loaded`
 * (`explorerConnectTelemetry.ts`).
 *
 * NOT `durationBucket`'s edges: that scale's lowest bucket is 5s, which puts
 * every healthy connect in one bucket and makes the metric useless. Edges are
 * FROZEN once released — moving one re-buckets the whole population invisibly.
 */
export type ConnectDurationBucket =
  | 'lt100ms'
  | 'lt250ms'
  | 'lt500ms'
  | 'lt1s'
  | 'lt2_5s'
  | 'lt5s'
  | 'lt10s'
  | 'gte10s';

/**
 * Bucket a measured connect-latency duration in milliseconds.
 *
 * A NaN or negative input clamps to the lowest bucket rather than falling
 * through to `gte10s`: broken clock arithmetic must not be reported as the
 * worst-case latency.
 */
export function connectDurationBucket(durationMs: number): ConnectDurationBucket {
  // Written as a negated `>=` so NaN takes this branch.
  if (!(durationMs >= 100)) return 'lt100ms';
  if (durationMs < 250) return 'lt250ms';
  if (durationMs < 500) return 'lt500ms';
  if (durationMs < 1_000) return 'lt1s';
  if (durationMs < 2_500) return 'lt2_5s';
  if (durationMs < 5_000) return 'lt5s';
  if (durationMs < 10_000) return 'lt10s';
  return 'gte10s';
}

/**
 * How long the app had been running when something happened — coarse enough
 * to separate "during startup / session restore" from "after an hour of use"
 * without identifying a session. Used by the hang events
 * (`app:renderer-unresponsive` / `-recovered`, rendererFreezeWatchdog.ts),
 * measured in main from process start. Edges are FROZEN once released, for
 * the same reason as every bucket above.
 */
export type SinceStartupBucket = 'lt1m' | '1to10m' | '10to60m' | 'gt60m';

/**
 * Bucket a since-startup duration in milliseconds. A NaN or negative input
 * clamps to the lowest bucket (broken clock arithmetic is not "long uptime").
 */
export function sinceStartupBucket(sinceStartupMs: number): SinceStartupBucket {
  // Written as a negated `>=` so NaN takes this branch.
  if (!(sinceStartupMs >= 60_000)) return 'lt1m';
  if (sinceStartupMs < 600_000) return '1to10m';
  if (sinceStartupMs < 3_600_000) return '10to60m';
  return 'gt60m';
}
